import { useEffect, useState } from "react";
import { Download, Wallet, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

// Historique des virements Stripe pour un restaurateur — dernier trimestre + export CSV.
export default function RestaurateurEarnings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/restaurateur/stripe-connect/earnings?month=${month}`);
        if (mounted) setData(data);
      } catch (e) {
        // silence — la carte se masque si pas approuvé/connecté
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [month]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/restaurateur/stripe-connect/earnings?month=${month}&fmt=csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `nold-go-revenus-${month}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export impossible.");
    } finally { setExporting(false); }
  };

  const openStripe = async () => {
    try {
      const { data } = await api.post("/restaurateur/stripe-connect/dashboard-link");
      if (data.url) window.open(data.url, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur Stripe.");
    }
  };

  if (loading || !data) return null;

  return (
    <div className="rounded-3xl bg-white border border-[#E2DFD8] p-5" data-testid="resto-earnings-card">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] font-bold text-[#75807A] uppercase tracking-wider inline-flex items-center gap-1.5">
            <Wallet className="w-3.5 h-3.5" /> Virements Stripe reçus
          </div>
          <div className="mt-1 flex items-end gap-2">
            <div className="font-head font-black text-3xl text-[#1A1A1A]" data-testid="resto-earnings-total">
              {(data.total_eur || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
            <div className="text-xs text-[#75807A] pb-1">{data.count} virement{data.count > 1 ? "s" : ""}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            data-testid="resto-earnings-month"
            className="h-9 px-3 rounded-lg border border-[#E2DFD8] text-sm"
          />
          <button onClick={exportCsv} disabled={exporting || data.count === 0}
            data-testid="resto-earnings-csv"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-white border border-[#E2DFD8] hover:border-[#F26419] text-xs font-bold text-[#1A1A1A] disabled:opacity-50">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            CSV
          </button>
          <button onClick={openStripe}
            data-testid="resto-earnings-stripe"
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#635BFF] hover:bg-[#5449D9] text-white text-xs font-bold">
            Stripe <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>
      {data.rows.length === 0 ? (
        <div className="text-sm text-[#75807A] text-center py-4">Aucun virement sur ce mois.</div>
      ) : (
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm" data-testid="resto-earnings-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#75807A] border-b border-[#E2DFD8]">
                <th className="text-left px-5 py-2">Date</th>
                <th className="text-left px-2 py-2">Client</th>
                <th className="text-right px-2 py-2">Plats (€)</th>
                <th className="text-right px-2 py-2">Nold Go (25%)</th>
                <th className="text-right px-5 py-2">Reçu</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.slice(0, 30).map((r) => (
                <tr key={r.order_id} className="border-b border-[#F0EBE1] hover:bg-[#FFF8ED]">
                  <td className="px-5 py-2 text-xs text-[#75807A] whitespace-nowrap">
                    {r.date ? new Date(r.date).toLocaleDateString("fr-FR") : "—"}
                  </td>
                  <td className="px-2 py-2 text-xs">{r.customer_name || "—"}</td>
                  <td className="px-2 py-2 text-right text-xs">{(r.subtotal_eur || 0).toFixed(2)}</td>
                  <td className="px-2 py-2 text-right text-xs text-[#75807A]">-{(r.commission_nold_go_eur || 0).toFixed(2)}</td>
                  <td className="px-5 py-2 text-right text-sm font-bold text-emerald-600">
                    {(r.virement_eur || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
